// src/components/alertesCritiques/DonneesSuspectes.jsx
// L'onglet « Données suspectes » des paramètres des alertes critiques
// (Outils administratifs > Alertes critiques), GO de Daniel du 16/10/2026.
//
// Ce qui mérite d'être vérifié avec l'école sans être une faute certaine. Cas
// d'origine, csap1 : la réduction d'EKOUELA Michel Béni, 1 748 FCFA, corrigée
// en 1 500 FCFA alors que l'alerte proposait 1 750 FCFA ; la mensualité monte
// de 248 FCFA, et la demande a été faite et validée par la même personne.
//
// Ce que montre l'onglet :
//   - la carte de chaque signal : sa règle en clair, son nombre de cas, son
//     interrupteur « Afficher dans le panneau des alertes » (éteint par
//     défaut) et, dessous, la case « Faire ouvrir et sonner le panneau (à
//     traiter) » (éteinte par défaut) : régime A, écriture immédiate, par le
//     même PUT que l'onglet Détecteurs ;
//   - un RÉCIT par cas (GO de Daniel du 17/10/2026 : trois lignes de chiffres
//     lui avaient fait croire à deux alertes) : l'élève, sa classe, sa
//     famille et le tarif de la classe quand il éclaire le calcul, puis
//     1. la saisie d'origine, 2. la correction, 3. le résultat, et ce qu'il
//     reste à vérifier avec l'école. Les montants et les écarts viennent tous
//     du serveur (`valeurs`) : l'écran les écrit, il n'en déduit aucun ;
//   - à côté, trois boutons de même taille, sans icône :
//     « Corriger » (la fenêtre de réduction de l'élève), « Ouvrir la demande »
//     (Centre de Support) et « Vérifié, c'est normal » (motif, Annuler /
//     Confirmer). Le « Vérifié » est le constat du panneau (POST
//     /api/alertes-critiques/constats) : un seul chemin d'écriture. Il est
//     refusé à la personne à l'origine de la demande, et réservé aux
//     validateurs du Centre de Support ; le serveur le dit pour chaque cas
//     (`constatable`, `concerne`). « Corriger » suit le `lien` du cas, celui
//     de la vue de l'appelant : la personne concernée n'en a pas, comme dans
//     le panneau (une seule règle, vuePourAppelant) ;
//   - « Déjà vérifiés », replié : le même récit, plus le motif, l'auteur et la
//     date du constat ;
//   - « Corrections validées par leur propre auteur », en lecture seule : une
//     ligne par agent depuis le premier jour de l'année, sans interrupteur ni
//     alerte ;
//   - un seul encadré, « Pourquoi suspecte et pas fausse ? », qui commence par
//     dire que ce n'est pas une deuxième alerte mais la suite de la première.
//
// Ouvrir l'onglet n'écrit RIEN (GET /api/parametres/alertes/donnees-suspectes,
// lecture pure, signaux calculés même éteints). Les données sont tenues par la
// page (useDonneesSuspectes) : Radix démonte l'onglet qu'on quitte. Elles se
// lisent à la première ouverture de l'onglet, et après une vérification.
//
// Peu d'icônes : la pastille de l'en-tête, le chevron du repli et le
// « Réessayer » d'un échec. Dates à l'heure de Paris, comme le suivi.

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, FileSearch, Loader2, Lock, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import EnTeteSection from '../parametrage/EnTeteSection';
import CarteReglages from '../parametrage/CarteReglages';
import ChargementSection from '../parametrage/ChargementSection';
import { Encadre, Interrupteur, LigneReglage } from '../parametrage/PiecesReglage';
import { constaterAlerte, lireDonneesSuspectes } from './apiAlertesCritiques';
import { anneeDeSession } from './useAlertesCritiques';
import { BOUTON_REESSAYER } from './SuiviAlertesCritiques';

/**
 * L'accent de l'onglet : framboise. Ni l'ambre du point « non enregistré »,
 * ni le violet du Suivi, le bleu des Réglages, le teal des Détecteurs, ni la
 * terre des anciens Seuils.
 */
export const ACCENT_SUSPECTES = '#a14a76';
const ACCENT_SUSPECTES_SOMBRE = '#843c61';

/** La catégorie des signaux de cet onglet (registre du serveur). */
export const CATEGORIE_SUSPECTES = 'donnees_suspectes';

const MOTIF_MIN = 10;
const MOTIF_MAX = 500;

// Les actions d'un cas : même taille, même largeur (la colonne), sans icône.
const BOUTON_CAS = 'h-8 w-full justify-center gap-2';
const BOUTON_PLEIN = 'rounded-lg bg-[#246990] text-white hover:bg-[#1f5a7a] focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Secondaire écrit en clair : la variante outline de ui/button sort transparente.
const BOUTON_SECONDAIRE = 'rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm '
  + 'hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Une consigne à la place d'un bouton, à sa taille.
const CONSIGNE = 'inline-flex min-h-8 w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-center text-xs font-medium text-gray-600';
const CONSIGNE_ATTENTE = 'inline-flex min-h-8 w-full items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-center text-xs font-medium text-amber-800';
const BOUTON_FORMULAIRE = 'min-w-[120px] justify-center gap-2';

const pluriel = (nombre, un, plusieurs) => (nombre > 1 ? plusieurs : un);

const estObjet = (valeur) => Boolean(valeur) && typeof valeur === 'object' && !Array.isArray(valeur);

// ============================================================================
// Dates : instants du serveur en UTC, écrits à l'heure de Paris
// ============================================================================

const MOTIF_INSTANT = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Un instant du serveur (ISO avec Z, ou sans fuseau : UTC), ou null. */
function lireInstant(valeur) {
  const m = MOTIF_INSTANT.exec(String(valeur || '').trim());
  if (!m) return null;
  const date = m[7]
    ? new Date(String(valeur).trim())
    : new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)));
  if (Number.isNaN(date.getTime())) return null;
  return { date, heure: m[4] !== undefined };
}

const FORMAT_JOUR_PARIS = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris'
});
const FORMAT_HEURE_PARIS = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris'
});
const FORMAT_JOUR_UTC = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'
});

/** « 16/10/2026 à 09:51 », à l'heure de Paris quel que soit le poste. */
const dateHeureParis = (valeur) => {
  const lu = lireInstant(valeur);
  if (!lu || !lu.heure) return null;
  return `${FORMAT_JOUR_PARIS.format(lu.date)} à ${FORMAT_HEURE_PARIS.format(lu.date)}`;
};

/** Le jour d'un instant, à Paris. */
const jourParis = (valeur) => {
  const lu = lireInstant(valeur);
  return lu && lu.heure ? FORMAT_JOUR_PARIS.format(lu.date) : null;
};

/** « 06/10/2026 » : une date seule du serveur (premier jour de l'année), sans glisser d'un jour. */
const dateSeule = (valeur) => {
  const lu = lireInstant(valeur);
  return lu ? FORMAT_JOUR_UTC.format(lu.date) : null;
};

/** « le 16/10/2026 », « les 14/10/2026 et 16/10/2026 », « les 12/10/2026, 14/10/2026 et 16/10/2026 ». */
function enumererJours(jours) {
  if (jours.length === 0) return '';
  if (jours.length === 1) return `le ${jours[0]}`;
  return `les ${jours.slice(0, -1).join(', ')} et ${jours[jours.length - 1]}`;
}

// ============================================================================
// Lecture
// ============================================================================

const INCOMPLETE = 'Réponse du serveur incomplète.';

/**
 * La réponse du serveur, vérifiée : une réponse incomplète est un échec de
 * lecture, jamais complétée par des listes vides présentées comme l'état réel.
 */
function preparerDonnees(reponse) {
  if (!estObjet(reponse) || !Array.isArray(reponse.signaux) || !Array.isArray(reponse.auto_validations)) {
    throw new Error(INCOMPLETE);
  }
  const signaux = reponse.signaux.filter(estObjet).map((signal) => {
    if (!Array.isArray(signal.cas) || !Array.isArray(signal.verifies)) throw new Error(INCOMPLETE);
    return signal;
  });
  return {
    genereLe: reponse.genere_le || null,
    droits: { verifier: Boolean(reponse.droits?.verifier) },
    depuis: typeof reponse.fenetre?.depuis === 'string' ? reponse.fenetre.depuis : null,
    signaux,
    autoValidations: reponse.auto_validations.filter(estObjet)
  };
}

/**
 * L'état de l'onglet, tenu par la page des paramètres. Rien n'est lu au
 * montage : la page appelle `charger` à la première ouverture de l'onglet.
 * Une lecture plus récente l'emporte sur une réponse en retard.
 *
 * @returns {{ donnees: object|null, chargement: boolean, erreur: string|null,
 *   refus: boolean, lu: boolean, charger: () => Promise<void> }}
 */
export function useDonneesSuspectes() {
  const [etat, setEtat] = useState({ donnees: null, chargement: false, erreur: null, refus: false, lu: false });
  const numeroRef = useRef(0);
  const monteRef = useRef(false);

  useEffect(() => {
    monteRef.current = true;
    return () => {
      monteRef.current = false;
    };
  }, []);

  const charger = useCallback(async () => {
    numeroRef.current += 1;
    const numero = numeroRef.current;
    const aJour = () => monteRef.current && numero === numeroRef.current;
    setEtat((precedent) => ({ ...precedent, chargement: true, lu: true }));
    try {
      const reponse = await lireDonneesSuspectes();
      if (reponse?.success === false) throw new Error(reponse.message || "Les données suspectes n'ont pas pu être lues.");
      const donnees = preparerDonnees(reponse);
      if (!aJour()) return;
      setEtat({ donnees, chargement: false, erreur: null, refus: false, lu: true });
    } catch (e) {
      if (!aJour()) return;
      // Lecture refusée : un droit qui manque, pas une panne à réessayer.
      if (e?.statut === 403) {
        setEtat({ donnees: null, chargement: false, erreur: null, refus: true, lu: true });
        return;
      }
      setEtat((precedent) => ({
        ...precedent,
        chargement: false,
        erreur: e?.message || "Les données suspectes n'ont pas pu être lues.",
        refus: false,
        lu: true
      }));
    }
  }, []);

  return { ...etat, charger };
}

// ============================================================================
// La carte d'un signal : règle, interrupteur, case « à traiter »
// ============================================================================

function CarteSignal({ signal, reglages, lectureSeule, ecrire, notifier, signalerEchec }) {
  // Valeur affichée pendant l'écriture ; vidée ensuite, l'état enregistré fait
  // foi : un refus du serveur ramène le contrôle où il était.
  const [optimiste, setOptimiste] = useState({});
  const [enCours, setEnCours] = useState(null);
  const caseId = useId();

  const actif = 'actif' in optimiste ? optimiste.actif : reglages?.detecteurs?.[signal.id] === true;
  const aTraiter = 'aTraiter' in optimiste ? optimiste.aTraiter : reglages?.a_traiter?.[signal.id] === true;
  const licence = signal.licence_ouverte !== false;
  const nombre = signal.cas.length;

  const enregistrer = async (champ, valeur, modifier) => {
    if (lectureSeule || enCours) return;
    setOptimiste({ [champ]: valeur });
    setEnCours(champ);
    const resultat = await ecrire(modifier, { changeSuivi: true });
    setEnCours(null);
    setOptimiste({});
    if (resultat.ok) notifier('Configuration mise à jour', 'success');
    else signalerEchec(resultat.message, 'Réglage refusé');
  };

  const basculer = () => {
    const valeur = !actif;
    enregistrer('actif', valeur, (base) => ({ ...base, detecteurs: { ...base.detecteurs, [signal.id]: valeur } }));
  };

  const cocher = () => {
    const valeur = !aTraiter;
    enregistrer('aTraiter', valeur, (base) => ({ ...base, a_traiter: { ...(base.a_traiter || {}), [signal.id]: valeur } }));
  };

  return (
    <CarteReglages
      titre={signal.titre}
      accent={ACCENT_SUSPECTES}
      compteur={licence && !signal.en_echec ? `${nombre} cas` : undefined}
      dense
    >
      <div className="px-5 py-4">
        <p className="max-w-3xl text-xs leading-relaxed text-gray-600">{signal.regle || signal.description}</p>
        {!licence && (
          <p className="mt-2 text-xs text-gray-500">
            La licence de l'établissement n'ouvre pas la comptabilité : ce signal n'est pas calculé.
          </p>
        )}
      </div>

      <LigneReglage
        actif={actif}
        accent={ACCENT_SUSPECTES}
        libelle="Afficher dans le panneau des alertes"
        // La description ne contredit jamais la case du dessous : « pour
        // information » ne vaut que tant qu'elle n'est pas cochée.
        description="Éteint, ces cas ne se voient que dans cette page. Allumé, ils entrent dans le panneau des agents habilités à gérer les alertes, pour information tant que la case ci-dessous n'est pas cochée : le panneau ne s'ouvre pas et ne sonne pas pour eux."
      >
        <Interrupteur
          actif={actif}
          accent={ACCENT_SUSPECTES}
          onBasculer={basculer}
          desactive={lectureSeule || Boolean(enCours) || !licence}
          enCours={enCours === 'actif'}
          libelle="Afficher dans le panneau des alertes"
        />
      </LigneReglage>

      {signal.option_a_traiter !== false && (
        <LigneReglage
          actif={actif && aTraiter}
          accent={ACCENT_SUSPECTES}
          libelle="Faire ouvrir et sonner le panneau (à traiter)"
          description={actif
            ? "Cochée, chaque cas compte « à traiter » : le panneau s'ouvre et sonne pour les agents habilités à gérer les alertes jusqu'à la correction ou la vérification. Jamais pour la personne qui a demandé ou validé la correction."
            : "Sans effet tant que le signal n'est pas affiché dans le panneau."}
        >
          <input
            id={caseId}
            type="checkbox"
            checked={aTraiter}
            onChange={cocher}
            disabled={lectureSeule || Boolean(enCours) || !actif || !licence}
            aria-label="Faire ouvrir et sonner le panneau (à traiter)"
            // La classe et le style disent la même couleur : l'accent de l'onglet.
            className={`h-5 w-5 cursor-pointer rounded border-gray-300 accent-[#a14a76] disabled:cursor-not-allowed ${enCours === 'aTraiter' ? 'opacity-40' : ''}`}
            style={{ accentColor: ACCENT_SUSPECTES }}
          />
        </LigneReglage>
      )}
    </CarteReglages>
  );
}

// ============================================================================
// Un cas
// ============================================================================

const nomOuAgent = (nom, id) => nom || (id ? `l'employé n° ${id}` : 'un agent');

// ----------------------------------------------------------------------------
// Le récit d'un cas (GO de Daniel du 17/10/2026)
// ----------------------------------------------------------------------------
//
//   EKOUELA Michel Béni - 3e - famille KOMBO BAKALA - tarif de la classe : 14 000 FCFA par mois
//   1. Saisie d'origine : réduction de 1 748 FCFA, sans doute un cran de molette (1 750 FCFA attendu).
//   2. Correction du 16/10/2026 (demande n° 53, demandée et validée par …) : réduction mise à
//      1 500 FCFA, et non à 1 750 FCFA.
//   3. Résultat : la famille paie 12 500 FCFA par mois au lieu de 12 250 FCFA, soit 248 FCFA de
//      plus qu'avant la correction au lieu de 2 FCFA de moins.
//   À vérifier avec l'école : quelle réduction est la bonne ? Si c'est 1 750 FCFA, « Corriger » ;
//   si c'est 1 500 FCFA, « Vérifié, c'est normal ».
//
// Tout vient du serveur : les montants, les deux écarts (`ecart`,
// `ecart_attendu`), le tarif et s'il éclaire le calcul (`tarif_eclaire` :
// faux sous un accord, avec une autre réduction en jeu, tarif inconnu).
// L'écran choisit seulement les mots : « de plus », « de moins ».

/** 1250000 -> « 1 250 000 FCFA », comme le serveur (formaterFcfa). */
const fcfa = (valeur) => {
  const n = Math.round(Number(valeur) || 0);
  return `${n < 0 ? '-' : ''}${String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} FCFA`;
};

/** Un groupe de mots qui ne se coupe pas en fin de ligne (téléphone) : montant, « Corriger »… */
const Insecable = ({ children }) => <span className="whitespace-nowrap">{children}</span>;
const Montant = ({ valeur }) => <Insecable>{fcfa(valeur)}</Insecable>;
const MontantGras = ({ children }) => <strong className="whitespace-nowrap font-semibold text-gray-900">{children}</strong>;
const Etiquette = ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>;

/** Un écart signé du serveur : « 248 FCFA de plus », « 2 FCFA de moins », « le même montant ». */
const texteEcart = (valeur) => {
  const n = Math.round(Number(valeur) || 0);
  if (n === 0) return 'le même montant';
  return `${fcfa(Math.abs(n))} de ${n > 0 ? 'plus' : 'moins'}`;
};

/** « demandée et validée par X », ou « demandée par X, validée par Y ». */
function quiACorrige(demande) {
  const demandeur = nomOuAgent(demande.demandeur_nom, demande.demandeur_id);
  if (demande.meme_personne) return `demandée et validée par ${demandeur}`;
  return `demandée par ${demandeur}, validée par ${nomOuAgent(demande.validateur_nom, demande.validateur_id)}`;
}

/** Un récit se compose seulement si le serveur a rendu ses montants et sa demande. */
const recitComplet = (cas) => estObjet(cas?.valeurs) && estObjet(cas?.demande)
  && Number.isInteger(Number(cas.demande.id)) && Number(cas.demande.id) > 0
  && ['avant', 'retenue', 'proposee', 'mensualite_apres', 'mensualite_attendue', 'ecart', 'ecart_attendu']
    .every((cle) => Number.isFinite(Number(cas.valeurs[cle])) && cas.valeurs[cle] !== null);

/** L'en-tête du cas : l'élève en gras, puis classe, famille et tarif quand il éclaire le calcul. */
function EnTeteCas({ cas }) {
  const v = cas.valeurs || {};
  const segments = [];
  if (cas.eleve?.classe_nom) segments.push(cas.eleve.classe_nom);
  if (cas.eleve?.famille_nom) segments.push(`famille ${cas.eleve.famille_nom}`);
  if (v.tarif_eclaire === true && Number(v.tarif_classe) > 0) {
    segments.push(<>tarif de la classe : <Montant valeur={v.tarif_classe} /> par mois</>);
  }
  return (
    <p className="break-words text-sm text-gray-500">
      <strong className="font-semibold text-gray-900">{cas.eleve?.nom || cas.libelle}</strong>
      {segments.map((segment, rang) => <React.Fragment key={rang}> - {segment}</React.Fragment>)}
    </p>
  );
}

/**
 * Le récit d'un cas. `boutons` : l'agent a « Corriger » ET « Vérifié, c'est
 * normal » sous la main, la conclusion peut les nommer ; sinon elle dit la
 * même chose sans renvoyer à un bouton absent. `verifie` : un cas de « Déjà
 * vérifiés », sans « À vérifier ».
 */
function RecitCas({ cas, boutons = false, verifie = false }) {
  if (!recitComplet(cas)) {
    // Un serveur qui n'a pas rendu le récit : ses textes du panneau, tels quels.
    return (
      <div className="min-w-0 space-y-1">
        <p className="break-words text-sm font-semibold text-gray-900">
          {cas.libelle}
          {cas.precision && <span className="font-normal text-gray-500"> - {cas.precision}</span>}
        </p>
        {cas.constat && <p className="break-words text-sm text-gray-700">{cas.constat}</p>}
        {cas.correction && <p className="break-words text-sm font-semibold text-gray-900">{cas.correction}</p>}
        {cas.impact && <p className="break-words text-xs text-gray-500">{cas.impact}</p>}
      </div>
    );
  }

  const v = cas.valeurs;
  const quoi = cas.composante_recit || 'réduction';
  const jour = jourParis(cas.demande.decidee_le);
  const attendu = Math.round(Number(v.ecart_attendu));

  return (
    <div className="min-w-0 space-y-1.5">
      <EnTeteCas cas={cas} />
      <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-gray-700 marker:font-semibold marker:text-gray-500">
        <li className="break-words pl-1">
          <Etiquette>Saisie d'origine :</Etiquette> {quoi} de <Montant valeur={v.avant} />, sans doute un cran de
          molette (<Montant valeur={v.proposee} /> attendu).
        </li>
        <li className="break-words pl-1">
          <Etiquette>Correction{jour ? ` du ${jour}` : ''}</Etiquette> (demande <Insecable>{`n° ${cas.demande.id}`}</Insecable>, {quiACorrige(cas.demande)}) :
          {' '}{quoi} mise à <MontantGras>{fcfa(v.retenue)}</MontantGras>, et non à <Montant valeur={v.proposee} />.
        </li>
        <li className="break-words pl-1">
          <Etiquette>Résultat :</Etiquette> la famille paie <MontantGras>{fcfa(v.mensualite_apres)}</MontantGras> par
          mois{v.accord ? " (part facturée sous l'accord financier)" : ''} au lieu de <Montant valeur={v.mensualite_attendue} />,
          soit <MontantGras>{texteEcart(v.ecart)}</MontantGras> qu'avant la correction
          {attendu === 0
            ? ' au lieu du même montant.'
            : <> au lieu de <Insecable>{texteEcart(attendu)}</Insecable>.</>}
        </li>
      </ol>
      {!verifie && (
        <p className="break-words text-sm leading-relaxed text-gray-700">
          <Etiquette>À vérifier avec l'école :</Etiquette> quelle {quoi} est la bonne ?{' '}
          {boutons
            ? (
              <>
                Si c'est <Montant valeur={v.proposee} />, <Insecable>« Corriger »</Insecable> ; si c'est <Montant valeur={v.retenue} />,
                {' '}<Insecable>« Vérifié, c'est normal »</Insecable>.
              </>
            )
            : <>Si c'est <Montant valeur={v.proposee} />, la {quoi} est à corriger ; si c'est <Montant valeur={v.retenue} />, c'est normal.</>}
        </p>
      )}
    </div>
  );
}

function FormulaireVerification({ motif, onMotif, envoi, onConfirmer, onAnnuler }) {
  const id = useId();
  const aide = useId();
  const longueur = motif.trim().length;
  const valide = longueur >= MOTIF_MIN;

  const soumettre = (evenement) => {
    evenement.preventDefault();
    if (valide && !envoi) onConfirmer();
  };

  return (
    <form onSubmit={soumettre} className="mt-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3">
      <label htmlFor={id} className="mb-1 block text-sm text-gray-700">Ce que l'école a confirmé (obligatoire)</label>
      <textarea
        id={id}
        rows={2}
        maxLength={MOTIF_MAX}
        autoFocus
        required
        aria-required="true"
        aria-describedby={aide}
        value={motif}
        onChange={(e) => onMotif(e.target.value)}
        placeholder="Par exemple : réduction ramenée à 1 500 FCFA à la demande de la direction"
        className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p id={aide} className="text-[11px] text-gray-500">
          {valide
            ? 'Le cas passera dans « Déjà vérifiés » tant que la valeur ne change pas.'
            : `Encore ${MOTIF_MIN - longueur} ${pluriel(MOTIF_MIN - longueur, 'caractère', 'caractères')} au moins.`}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" className={`${BOUTON_FORMULAIRE} ${BOUTON_SECONDAIRE}`} onClick={onAnnuler} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" size="sm" className={`${BOUTON_FORMULAIRE} ${BOUTON_PLEIN}`} disabled={!valide || envoi}>
            {envoi && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Pourquoi le « Vérifié » n'est pas proposé : null s'il l'est. */
function consigneVerification(cas, { verifier, anneeCloturee }) {
  if (cas.concerne) return 'Vous avez demandé ou validé cette correction : un autre agent la vérifie ou la corrige';
  if (!verifier) return 'Vérification réservée aux validateurs du Centre de Support';
  if (anneeCloturee) return 'Session ouverte sur une année clôturée : vérification impossible ici';
  if (cas.constatable !== true) return 'Ce cas ne peut pas être vérifié';
  return null;
}

function LigneCas({
  cas, anneeSession, verifier, anneeCloturee, enVerification, motif, envoi,
  onMotif, onVerifier, onConfirmer, onAnnuler, onNaviguer
}) {
  const autreAnnee = cas.annee_scolaire_id != null && anneeSession != null
    && String(cas.annee_scolaire_id) !== String(anneeSession);
  const consigne = consigneVerification(cas, { verifier, anneeCloturee });

  let correction;
  if (cas.action === 'attendre_validation') {
    correction = <span className={CONSIGNE_ATTENTE}>Correction envoyée - en attente de validation</span>;
  } else if (autreAnnee) {
    correction = <span className={CONSIGNE}>À corriger dans une session ouverte sur l'année {cas.annee_nom || cas.annee_scolaire_id}</span>;
  } else if (cas.lien) {
    // Le lien de la vue de l'appelant (vuePourAppelant) : la personne
    // concernée n'en a pas, comme dans le panneau.
    correction = (
      <Button type="button" size="sm" className={`${BOUTON_CAS} ${BOUTON_PLEIN}`} onClick={() => onNaviguer(cas.lien)} disabled={envoi}>
        Corriger
      </Button>
    );
  }
  // La conclusion du récit ne nomme les boutons que si l'agent les a tous deux.
  const boutons = cas.action !== 'attendre_validation' && !autreAnnee && Boolean(cas.lien) && !consigne;

  return (
    <div className="px-5 py-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_12.5rem] lg:items-start">
        <RecitCas cas={cas} boutons={boutons} />

        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          {correction}
          {cas.demande?.lien && (
            <Button type="button" size="sm" className={`${BOUTON_CAS} ${BOUTON_SECONDAIRE}`} onClick={() => onNaviguer(cas.demande.lien)} disabled={envoi}>
              Ouvrir la demande
            </Button>
          )}
          {consigne
            ? <span className={CONSIGNE}>{consigne}</span>
            : !enVerification && (
              <Button type="button" size="sm" className={`${BOUTON_CAS} ${BOUTON_SECONDAIRE}`} onClick={onVerifier} disabled={envoi}>
                Vérifié, c'est normal
              </Button>
            )}
        </div>
      </div>

      {enVerification && (
        <FormulaireVerification
          motif={motif}
          onMotif={onMotif}
          envoi={envoi}
          onConfirmer={onConfirmer}
          onAnnuler={onAnnuler}
        />
      )}
    </div>
  );
}

// ============================================================================
// Déjà vérifiés (replié)
// ============================================================================

function DejaVerifies({ verifies }) {
  const [ouvert, setOuvert] = useState(false);
  const nombre = verifies.length;
  const zoneId = useId();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <button
        type="button"
        onClick={() => setOuvert((valeur) => !valeur)}
        disabled={nombre === 0}
        aria-expanded={ouvert}
        aria-controls={zoneId}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-gray-50/70 disabled:cursor-default disabled:hover:bg-white"
      >
        <span className="h-8 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: ACCENT_SUSPECTES }} />
        <span className="text-[13px] font-bold uppercase tracking-wide text-gray-800">Déjà vérifiés</span>
        <span className="ml-auto whitespace-nowrap rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-400">
          {nombre === 0 ? 'aucun' : `${nombre} cas`}
        </span>
        {nombre > 0 && (
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${ouvert ? 'rotate-180' : ''}`} aria-hidden="true" />
        )}
      </button>
      {ouvert && nombre > 0 && (
        <div id={zoneId} className="divide-y divide-gray-50 border-t border-gray-100">
          {verifies.map((cas) => {
            const quand = dateHeureParis(cas.verification?.le);
            const par = cas.verification?.par_nom || (cas.verification?.par_id ? `l'employé n° ${cas.verification.par_id}` : null);
            return (
              <div key={`${cas.cle}|${cas.empreinte}`} className="space-y-1.5 px-5 py-4">
                <RecitCas cas={cas} verifie />
                {/* Le motif : ce que l'école a confirmé. */}
                <p className="break-words text-sm leading-relaxed text-gray-700">
                  <Etiquette>Vérifié</Etiquette>{par ? ` par ${par}` : ''}{quand ? ` le ${quand}` : ''}
                  {cas.verification?.motif ? ` : « ${cas.verification.motif} »` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Corrections validées par leur propre auteur (lecture seule)
// ============================================================================

function CarteAutoValidations({ agents, depuis, onNaviguer }) {
  const debut = dateSeule(depuis);
  return (
    <CarteReglages
      titre="Corrections validées par leur propre auteur"
      accent={ACCENT_SUSPECTES}
      compteur={`${agents.length} ${pluriel(agents.length, 'agent', 'agents')}`}
      dense
    >
      {agents.length === 0 && (
        <p className="px-5 py-6 text-xs text-gray-400">
          {debut
            ? `Aucune correction validée par la personne qui l'a demandée depuis le ${debut}.`
            : "Aucune correction validée par la personne qui l'a demandée."}
        </p>
      )}
      {agents.map((agent) => {
        const demandes = Array.isArray(agent.demandes) ? agent.demandes : [];
        const jours = [...new Set(demandes.map((d) => jourParis(d.decidee_le)).filter(Boolean))];
        const nombre = Number(agent.nombre) || demandes.length;
        return (
          <div key={agent.employe_id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
            <div className="min-w-[200px]">
              <p className="text-sm font-semibold text-gray-800">{agent.nom}</p>
              <p className="mt-0.5 text-xs text-gray-500">
                {nombre} {pluriel(nombre, 'demande', 'demandes')}{jours.length > 0 ? ` - ${enumererJours(jours)}` : ''}
              </p>
            </div>
            {/* Sous le nom (écran étroit) : à gauche pour tous les agents ;
                à côté du nom : à droite, poussées par ml-auto même quand la
                ligne passe dessous. */}
            <div className="flex max-w-xl flex-wrap justify-start gap-1.5 sm:ml-auto sm:justify-end">
              {demandes.map((demande) => (
                <button
                  key={demande.id}
                  type="button"
                  onClick={() => onNaviguer(demande.lien)}
                  aria-label={`Ouvrir la demande n° ${demande.id} (${demande.libelle})`}
                  className="inline-flex h-7 items-center rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-semibold text-[#246990] transition-colors hover:border-[#246990]/30 hover:bg-[#eef7fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40"
                >
                  n° {demande.id}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <p className="px-5 py-2.5 text-[11px] leading-relaxed text-gray-400">
        {debut ? `Depuis le ${debut}, premier jour de l'année. ` : ''}
        Consultation seule : ni interrupteur, ni alerte. Les annulations privilégiées d'avance et les
        ouvertures de fenêtre de paie n'y figurent pas : elles ont leur propre circuit (double
        authentification et journal).
      </p>
    </CarteReglages>
  );
}

// ============================================================================
// L'onglet
// ============================================================================

/**
 * La première phrase de « Pourquoi suspecte et pas fausse ? » : ce n'est pas
 * une deuxième alerte. Un seul cas affiché, dont la valeur retenue est un
 * montant rond (le serveur le dit : `retenue_ronde`) : ses montants. Sinon
 * une phrase générale, vraie pour chaque cas.
 */
function phraseSuite(signaux) {
  const tous = signaux.flatMap((signal) => [...signal.cas, ...signal.verifies]);
  const seul = tous.length === 1 && recitComplet(tous[0]) && tous[0].valeurs.retenue_ronde === true ? tous[0] : null;
  const debut = "Ce n'est pas une deuxième alerte : c'est la suite de la première.";
  const fin = 'ce signal regarde si la correction a mis la bonne valeur.';
  if (seul) return `${debut} L'alerte de molette s'est éteinte parce que ${fcfa(seul.valeurs.retenue)} est un montant rond ; ${fin}`;
  return `${debut} L'alerte de molette s'éteint dès que la valeur corrigée n'est plus à quelques francs d'un montant rond ; ${fin}`;
}

/**
 * L'onglet « Données suspectes ». `sansDroit` : la page sait déjà que
 * l'agent n'a pas la porte (droits.modifier) ; rien n'est lu.
 */
export default function DonneesSuspectes({
  donnees, chargement, erreur, refus, charger,
  sansDroit = false, reglages, lectureSeule, anneeCloturee = false,
  ecrire, notifier, signalerEchec, onVerification
}) {
  const navigate = useNavigate();
  const anneeSession = useMemo(() => anneeDeSession(), []);
  const [enVerification, setEnVerification] = useState(null); // `${signal}|${cle}`
  const [motifs, setMotifs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const envoiEnCours = useRef(false);

  const refuse = sansDroit || refus;

  const oublier = (cle) => setMotifs((precedents) => {
    if (!(cle in precedents)) return precedents;
    const suivants = { ...precedents };
    delete suivants[cle];
    return suivants;
  });

  const confirmer = async (signal, cas) => {
    const cle = `${signal.id}|${cas.cle}`;
    const motif = (motifs[cle] || '').trim();
    if (motif.length < MOTIF_MIN || envoiEnCours.current) return;
    envoiEnCours.current = true;
    setEnvoi(true);
    try {
      await constaterAlerte({
        detecteur: signal.id,
        entite_type: cas.entite_type,
        entite_id: cas.entite_id,
        empreinte: cas.empreinte,
        motif
      });
      setEnVerification(null);
      oublier(cle);
      notifier('Vérification enregistrée : le cas passe dans « Déjà vérifiés ».', 'success');
      if (onVerification) onVerification();
      await charger();
    } catch (e) {
      if (e?.statut === 409) {
        setEnVerification(null);
        oublier(cle);
        if (e.code === 'DEJA_CONSTATEE') notifier('Ce cas avait déjà été vérifié.', 'info');
        else signalerEchec("La situation a changé depuis l'affichage de la page : elle vient d'être relue.", 'Vérification impossible');
        await charger();
      } else {
        signalerEchec(e?.message || "La vérification n'a pas pu être enregistrée.", 'Vérification impossible');
      }
    } finally {
      envoiEnCours.current = false;
      setEnvoi(false);
    }
  };

  const signaux = donnees?.signaux || [];

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={FileSearch}
        titre="Données suspectes"
        sousTitre="Ce qui mérite d'être vérifié avec l'école sans être une faute certaine. Rien n'entre dans le panneau sans votre accord."
        accent={ACCENT_SUSPECTES}
        accentSombre={ACCENT_SUSPECTES_SOMBRE}
      />

      {refuse && (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
          <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />
          <p>Les données suspectes sont réservées aux agents habilités à gérer les alertes critiques.</p>
        </div>
      )}

      {!refuse && chargement && !donnees && (
        <ChargementSection accent={ACCENT_SUSPECTES} libelle="Chargement des données suspectes…" />
      )}

      {!refuse && !chargement && erreur && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0 text-sm text-red-800">
            <p className="font-semibold text-red-900">
              {donnees ? "L'actualisation des données suspectes a échoué" : "Les données suspectes n'ont pas pu être lues"}
            </p>
            <p className="mt-1">{erreur}</p>
            <button type="button" onClick={charger} className={BOUTON_REESSAYER}>
              <RefreshCw size={15} />
              Réessayer
            </button>
          </div>
        </div>
      )}

      {!refuse && donnees && (
        <>
          {signaux.map((signal) => (
            <React.Fragment key={signal.id}>
              <CarteSignal
                signal={signal}
                reglages={reglages}
                lectureSeule={lectureSeule}
                ecrire={ecrire}
                notifier={notifier}
                signalerEchec={signalerEchec}
              />

              <CarteReglages
                titre="Cas à vérifier"
                accent={ACCENT_SUSPECTES}
                compteur={signal.en_echec ? undefined : `${signal.cas.length} cas`}
                dense
              >
                {signal.en_echec && (
                  <p className="px-5 py-6 text-xs text-amber-800">
                    La vérification n'a pas abouti : ses cas manquent ici. Réessayez dans un moment.
                  </p>
                )}
                {!signal.en_echec && signal.cas.length === 0 && (
                  <p className="px-5 py-6 text-xs text-gray-400">
                    {dateSeule(donnees.depuis)
                      ? `Aucun cas à vérifier depuis le ${dateSeule(donnees.depuis)}.`
                      : 'Aucun cas à vérifier.'}
                  </p>
                )}
                {signal.cas.map((cas) => {
                  const cle = `${signal.id}|${cas.cle}`;
                  return (
                    <LigneCas
                      key={cle}
                      cas={cas}
                      anneeSession={anneeSession}
                      verifier={donnees.droits.verifier}
                      anneeCloturee={anneeCloturee}
                      enVerification={enVerification === cle}
                      motif={motifs[cle] || ''}
                      envoi={envoi}
                      onMotif={(texte) => setMotifs((precedents) => ({ ...precedents, [cle]: texte }))}
                      onVerifier={() => { if (!envoiEnCours.current) setEnVerification(cle); }}
                      onConfirmer={() => confirmer(signal, cas)}
                      onAnnuler={() => {
                        if (envoiEnCours.current) return;
                        oublier(cle);
                        setEnVerification(null);
                      }}
                      onNaviguer={(lien) => navigate(lien)}
                    />
                  );
                })}
              </CarteReglages>

              <DejaVerifies verifies={signal.verifies} />
            </React.Fragment>
          ))}

          <CarteAutoValidations
            agents={donnees.autoValidations}
            depuis={donnees.depuis}
            onNaviguer={(lien) => navigate(lien)}
          />

          <Encadre titre="Pourquoi suspecte et pas fausse ?">
            <p className="font-medium text-gray-700">{phraseSuite(signaux)}</p>
            <p className="mt-2">
              La valeur que proposait l'alerte est le montant rond le plus proche de l'ancienne saisie : une
              présomption, pas une certitude. En corrigeant, l'école a pu vouloir une autre valeur. Seul
              l'échange avec elle tranche : « Corriger » si c'était une faute (une nouvelle demande, validée
              de préférence par une autre personne), « Vérifié, c'est normal » avec ce qu'elle a confirmé
              sinon. Une vérification vaut pour cette valeur : si elle change, le cas revient.
            </p>
          </Encadre>
        </>
      )}
    </div>
  );
}
